import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  CardContent,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';
import ManagementApi from '../../../apis/ManagementApi';
import PaymentsApi from '../../../apis/PaymentsApi';
import SnackbarContext from '../../../contexts/SnackbarContext';
import useApi from '../../../lib/useApi';
import ContactUsDialog from '../../ContactUsDialog/ContactUsDialog';
import LoadingCircle from '../../LoadingCircle/LoadingCircle';
import ManagementGridProps from '../lib/ManagementGridProps';
import AddLicensesDialog from './AddLicenseDialog/AddLicenseDialog';
import EditManagersDialog from './EditManagerDialog';
import EditRequiredTrainingDialog from './EditRequiredTrainingDialog';

/**
 * The ActionCard component displays the organization name and action buttons.
 */
const ActionCard = (props: ManagementGridProps) => {
  const [organizationName, setOrganizationName] = useState<string>('');
  const [showEditManagersDialog, setShowEditManagersDialog] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showContactDialog, setShowContactDialog] = useState<boolean>(false);
  const managementApi = useApi(ManagementApi);
  const theme = useTheme();
  const buttonVariant = theme.palette.mode == 'light' ? 'primary' : 'secondary';
  const [showEditTrainingDialog, setShowEditTrainingDialog] = useState(false);
  const [showAddLicenseDialog, setShowAddLicenseDialog] = useState(false);
  const [creatingPortal, setCreatingPortal] = useState(false);
  const paymentApi = useApi(PaymentsApi);
  const snackbar = useContext(SnackbarContext);

  /**
   * Fetch the organization name when the component mounts.
   */
  useEffect(() => {
    const getOrgNamePromise = props.orgId
      ? managementApi.GetOrganizationNameWithOrgId(props.orgId)
      : managementApi.GetOrganizationName();
    getOrgNamePromise
      .then(setOrganizationName)
      .catch((error) => {
        console.error('Failed to fetch organization name:', error);
      })
      .finally(() => setIsLoading(false));
  }, [managementApi, props.orgId]);

  /** Create the customer portal */
  const createCustomerPortal = useCallback(() => {
    setCreatingPortal(true);
    paymentApi
      .CreatePortalSession({ returnUrl: window.location.href })
      .then((p) => (window.location.href = p.url))
      .catch(() => {
        snackbar.setAlert(
          <Alert severity="error" variant="filled">
            An error occurred while creating the customer portal. If this
            continues, please contact us.
          </Alert>
        );
        snackbar.triggerSnackbar();
        setCreatingPortal(false);
      });
  }, [paymentApi, snackbar]);

  return (
    <CardContent sx={{ height: '100%', width: '100%' }}>
      {isLoading ? (
        <LoadingCircle isLoading={isLoading} />
      ) : (
        <Box
          height="100%"
          display="flex"
          flexDirection="column"
          justifyContent="space-around"
        >
          <Box width="100%">
            {/* Organization Name */}
            <Typography
              variant="h2"
              textAlign="center"
              sx={{
                overflowWrap: 'break-word',
                fontSize: organizationName.length > 30 ? '2.3em' : '4em',
              }}
            >
              {organizationName}
            </Typography>
            <Typography variant="h5" gutterBottom textAlign="center">
              Management Dashboard
            </Typography>
          </Box>

          {!props.orgId && (
            <>
              {/* Action Buttons */}
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="center"
                width="100%"
                gap={2}
                mt={2}
                flexWrap="wrap"
              >
                {/* Contact Us Button */}
                <Button
                  variant="outlined"
                  color={buttonVariant}
                  onClick={() => setShowContactDialog(true)}
                >
                  Contact Support
                </Button>

                {/* Modify the required training button */}
                <Button
                  variant="outlined"
                  color={buttonVariant}
                  onClick={() => setShowEditTrainingDialog(true)}
                >
                  Edit Required Training
                </Button>

                {/* Modify the required training button */}
                <Button
                  variant="outlined"
                  color={buttonVariant}
                  onClick={() => setShowEditManagersDialog(true)}
                >
                  Edit Managers
                </Button>

                {/* Add Licenses Button */}
                <Button
                  variant="outlined"
                  color={buttonVariant}
                  onClick={() => setShowAddLicenseDialog(true)}
                >
                  Add Licenses
                </Button>

                {/* The button to take the user to the customer portal page */}
                <LoadingButton
                  variant="outlined"
                  color={buttonVariant}
                  onClick={createCustomerPortal}
                  loading={creatingPortal}
                >
                  Manage Subscription
                </LoadingButton>
              </Box>

              {/* Contact Us Dialog */}
              <ContactUsDialog
                open={showContactDialog}
                onClose={() => setShowContactDialog(false)}
              />

              {/* Edit Required Training Dialog */}
              <EditRequiredTrainingDialog
                open={showEditTrainingDialog}
                onClose={() => setShowEditTrainingDialog(false)}
              />

              {/* Edit Managers Dialog */}
              <EditManagersDialog
                open={showEditManagersDialog}
                onClose={() => setShowEditManagersDialog(false)}
              />

              {/* Add License Dialog */}
              <AddLicensesDialog
                open={showAddLicenseDialog}
                onClose={() => setShowAddLicenseDialog(false)}
              />
            </>
          )}
        </Box>
      )}
    </CardContent>
  );
};

export default ActionCard;
